/**
 * Minimum-order fee reconciler — enforces the $35 cart minimum on aftermarket carts.
 *
 * THE RULE (Dan, 2026-09-04): aftermarket carts have a $35 minimum. A cart whose qualifying
 * total is under the minimum gets one $10 "Minimum Cart Fee" line. At or above the minimum there
 * is no fee. A cart mixing aftermarket and OEM parts is judged on the WHOLE cart total, not the
 * aftermarket subtotal — so $20 of AM plus $20 of OEM is $40 and pays nothing.
 *
 * This is the third in-house cart reconciler in the theme and is modelled directly on
 * assets/core-deposit.js: ONE idempotent pass per cart change, mutations STRICTLY SEQUENTIAL
 * (never concurrent, never Promise.all) so we don't trip Shopify's cart rate limit, and every
 * fetch FAILS OPEN — a hiccup never blocks add-to-cart or checkout, and self-heals on the next
 * cart event. See core-deposit-build-plan.md for why that discipline exists (the UpCharge 429
 * storm and its stuck overlay).
 *
 * ── The three properties that make this correct ──────────────────────────────────────────────
 *
 * 1. NO OSCILLATION. The qualifying total sums `original_line_price` over lines that are neither
 *    a fee line nor a core-deposit line. Because the fee is excluded from the total by
 *    construction, adding or removing it cannot change the total, so the decision is a constant
 *    across our own mutations and one pass reaches the fixed point. `original_line_price` (rather
 *    than final_line_price) also makes the total invariant under discount RE-ALLOCATION: a
 *    fixed-amount cart discount re-spreads across lines when the fee joins the cart, which with
 *    final_line_price opens a band where remove → under → add → over → remove loops forever.
 *
 * 2. NO STRANDED FEE. `total > 0` is load-bearing: a cart holding nothing but a fee line has a
 *    qualifying total of 0, so the fee is removed rather than left behind as an un-removable $10
 *    line. snippets/cart-products.liquid and snippets/cart-summary.liquid additionally treat a
 *    fee-only cart as empty and disable checkout, so a JS failure can't strand the customer. And
 *    when the feature is switched off (or misconfigured) with fee lines already in carts,
 *    snippets/scripts.liquid loads this module in REMOVE-ONLY mode to take them out.
 *
 * 3. WE ONLY DELETE OUR OWN LINES. Deciding what to EXCLUDE from the total is permissive (the
 *    `_min_order_fee` property OR the SKU prefix); deciding what to MUTATE is strict (the SKU
 *    prefix only). assets/pickup-selector.js rewrites every line's properties addressed by
 *    1-based line index read from an earlier /cart.js, so a mutation landing mid-loop can stamp
 *    one line's properties onto another. Keying deletion on the SKU prefix means that race can
 *    never make us delete a customer's part.
 *
 * ── Why the pass alone isn't enough: Horizon's cart addresses lines by POSITION ─────────────────
 * The cart rows remove and re-quantity lines by 1-based INDEX, and the cart form posts updates[]
 * by POSITION. Shopify puts a newly added line at the TOP, so adding the fee shifts every rendered
 * row one line off until the re-render lands. Two guards cover that window (see below): the rows
 * go inert while a fee change is in flight, and Checkout waits for the cart to settle — then
 * submits without the positional quantities — so it can neither skip a re-decision nor smear a
 * part's quantity onto the fee.
 *
 * ── Aftermarket detection ────────────────────────────────────────────────────────────────────
 * /cart.js exposes no tags and no metafields, so the server-side source of truth
 * (snippets/part-source.liquid: cross_variant.role == 'AM', or the exact AFTERMARKET tag) is not
 * reachable here. The SKU prefix is, and it was measured against the full cohort on 2026-09-04:
 * of 19,296 products that are aftermarket by the theme's own rules, 19,284 carry `AM:` and 12
 * carry `FG:` (the Fleetguard companion listings) — and ZERO products outside that cohort carry
 * an `AM:` SKU. So AM_PREFIXES below is exact today, not a heuristic. If a future load introduces
 * an aftermarket SKU prefix, ADD IT HERE — nothing else in this module needs to change.
 */

import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { debounce } from '@theme/utilities';

/** Tag our own re-render dispatch so our listener ignores it (no reconcile loop). */
const SOURCE = 'min-order-fee';

/** The fee product's variants are SKU'd cents-based: MIN-ORDER-FEE-<cents>. */
const FEE_SKU_PREFIX = 'MIN-ORDER-FEE-';
/** Hidden line-item property this module stamps on the fee line. */
const P_FEE = '_min_order_fee';

/** Core-deposit lines are excluded from the qualifying total — see readConfig().countDeposits. */
const DEPOSIT_SKU_PREFIX = 'CORE-DEP-';
const P_DEPOSIT = '_core_deposit';

/** Aftermarket SKU prefixes. Measured complete 2026-09-04 — see the header comment. */
const AM_PREFIXES = ['AM:', 'FG:'];

/**
 * Server-rendered configuration (snippets/scripts.liquid, gated on snippets/min-order-fee-live.liquid).
 * Two shapes reach the browser:
 *   - the fee is operating:  {variant_id, threshold_cents, scope, count_deposits}
 *   - it is NOT operating, but this cart still holds a fee line:  {"mode": "remove"}
 * A missing or unreadable config means there is nothing for this module to do.
 * @returns {{mode: 'fee'|'remove', variantId: number, thresholdCents: number, scope: string, countDeposits: boolean}|null}
 */
function readConfig() {
  const el = document.getElementById('min-order-fee-config');
  if (!el) return null;
  try {
    const cfg = JSON.parse(el.textContent || '{}');
    if (cfg.mode === 'remove') {
      return { mode: 'remove', variantId: 0, thresholdCents: 0, scope: 'aftermarket', countDeposits: false };
    }
    const variantId = Number(cfg.variant_id);
    const thresholdCents = Math.round(Number(cfg.threshold_cents));
    if (!variantId || !Number.isFinite(thresholdCents) || thresholdCents <= 0) return null;
    return {
      mode: 'fee',
      variantId,
      thresholdCents,
      scope: cfg.scope === 'all' ? 'all' : 'aftermarket',
      countDeposits: cfg.count_deposits === true,
    };
  } catch (_e) {
    return null;
  }
}

const CONFIG = readConfig();

const sku = (line) => String((line && line.sku) || '');
const pause = (ms) => new Promise((res) => setTimeout(res, ms));

/** Every cart request gives up after this long: fail-open covers a request that ERRORS, and this
 *  makes a request that merely HANGS (a stalled mobile connection) fail too, instead of holding a
 *  pass — or the Checkout button — indefinitely. */
const FETCH_TIMEOUT_MS = 8000;
const withTimeout = () =>
  typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) }
    : {};

/** STRICT — the only lines this module is allowed to change or delete: the fee SKU prefix, or (in
 *  fee mode) the exact variant this module adds. Without the second test, a fee product whose SKU
 *  lacked the prefix would never be recognised as already present and would be re-added on every
 *  pass (snippets/min-order-fee-live.liquid also refuses to go live on such a product). */
function isFeeLine(line) {
  if (sku(line).startsWith(FEE_SKU_PREFIX)) return true;
  return Boolean(CONFIG && CONFIG.mode === 'fee' && line && String(line.variant_id) === String(CONFIG.variantId));
}

/** PERMISSIVE — anything that looks like a fee line is kept out of the qualifying total. */
function looksLikeFeeLine(line) {
  return isFeeLine(line) || Boolean(line && line.properties && line.properties[P_FEE]);
}

function isDepositLine(line) {
  return sku(line).startsWith(DEPOSIT_SKU_PREFIX) || Boolean(line && line.properties && line.properties[P_DEPOSIT]);
}

/** Is this cart line an aftermarket part? */
function isAftermarketLine(line) {
  const s = sku(line);
  return AM_PREFIXES.some((p) => s.startsWith(p));
}

/** Lines that count toward the minimum: real parts only — never the fee, and (by default) never a
 *  refundable core deposit. Deposits run $0.01–$15,225, so counting them would exempt essentially
 *  every remanufactured part from the minimum. */
function qualifyingLines(cart) {
  return cart.items.filter((l) => !looksLikeFeeLine(l) && (CONFIG.countDeposits || !isDepositLine(l)));
}

/** Qualifying total in cents. Pre-discount by design — see property (1) in the header. */
function qualifyingTotal(cart) {
  return qualifyingLines(cart).reduce((sum, l) => sum + (Number(l.original_line_price) || 0), 0);
}

/** Cart unit count EXCLUDING auto-managed lines — what the cart-count badge should show. */
function realCount(cart) {
  return cart.items.reduce((n, l) => (looksLikeFeeLine(l) || isDepositLine(l) ? n : n + l.quantity), 0);
}

/** Should this cart carry the fee right now? Never, in remove-only mode. */
function wantsFee(cart) {
  if (CONFIG.mode === 'remove') return false;
  const total = qualifyingTotal(cart);
  if (total <= 0 || total >= CONFIG.thresholdCents) return false;
  if (CONFIG.scope === 'all') return true;
  return qualifyingLines(cart).some(isAftermarketLine);
}

/**
 * What it takes to bring `cart` to exactly one correct fee line (or none). Pure — used both to
 * drive a pass and to check whether the cart it produced actually settled.
 */
function planFor(cart) {
  const feeLines = cart.items.filter(isFeeLine);
  // The keeper is a fee line already priced at the configured variant. A fee line on any other
  // variant (the amount was changed in the theme editor) is replaced rather than repriced.
  const keeper = wantsFee(cart) ? feeLines.find((l) => String(l.variant_id) === String(CONFIG.variantId)) : undefined;
  const remove = feeLines.filter((l) => l !== keeper);
  // The fee is one per cart, never per unit. cart-dedup.js merges two identical fee lines into one
  // at qty 2 rather than dropping one, so the keeper is forced back to 1.
  const fixQty = keeper && keeper.quantity !== 1 ? keeper : null;
  const add = wantsFee(cart) && !keeper;
  return { remove, fixQty, add, changes: remove.length > 0 || Boolean(fixQty) || add };
}

/**
 * GET the current cart, or null on failure (fail-open).
 * @returns {Promise<{items: any[], item_count: number}|null>}
 */
async function getCart() {
  try {
    const r = await fetch('/cart.js', { credentials: 'same-origin', ...withTimeout() });
    if (!r.ok) return null;
    const cart = await r.json();
    return cart && Array.isArray(cart.items) ? cart : null;
  } catch (_e) {
    return null;
  }
}

/**
 * One cart mutation with a single 429 retry, then fail-open. Never throws.
 * @param {string} url - '/cart/add.js' | '/cart/change.js'
 * @param {object} body
 * @returns {Promise<boolean>} whether the mutation succeeded
 */
async function mutateOnce(url, body) {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
        ...withTimeout(),
      });
      if (r.ok) return true;
      if (r.status === 429 && attempt === 0) {
        await pause(800);
        continue; // retry once, then give up
      }
      return false;
    } catch (_e) {
      return false;
    }
  }
  return false;
}

// ── Row lock while a fee change is in flight ────────────────────────────────────────────────────
// From our mutation until the re-render lands, every rendered row can point one line off (see the
// header). Rows are made inert for exactly that window — released when the rendered rows show the
// cart we just produced, or after BUSY_MAX_MS so a lost re-render can never leave the cart stuck.
// Checkout is deliberately NOT locked here: a dead button is worse than a short wait, and its own
// guard (below) already waits for the cart to settle.

const BUSY_ATTR = 'data-min-order-fee-busy';
const BUSY_MAX_MS = 3000;
/** Each mutating pass holds its own lock; the rows only come back when NO pass holds one, so a
 *  later pass can never release an earlier pass's lock before that pass's re-render lands. */
const activeLocks = new Set();
let lockSeq = 0;

function setBusy() {
  const token = ++lockSeq;
  activeLocks.add(token);
  document.documentElement.setAttribute(BUSY_ATTR, '');
  setTimeout(() => releaseBusy(token), BUSY_MAX_MS + 500); // backstop — never stuck
  return token;
}

function releaseBusy(token) {
  if (!activeLocks.delete(token)) return;
  if (!activeLocks.size) document.documentElement.removeAttribute(BUSY_ATTR);
}

/** Do the rendered cart rows (drawer and/or cart page) list exactly the lines of `cart`? */
function rowsShow(cart) {
  const rows = document.querySelectorAll('tr.cart-items__table-row[data-key]');
  if (!rows.length) return true; // nothing rendered here, so nothing can be stale
  const shown = new Set(Array.from(rows, (r) => r.getAttribute('data-key')));
  const want = new Set(cart.items.map((l) => l.key));
  return shown.size === want.size && [...want].every((k) => shown.has(k));
}

/**
 * Fresh HTML for every rendered cart section, via the Section Rendering API — the same thing
 * Horizon's own quantity change asks /cart/change.js for. Handing it over in the event matters:
 * without `sections`, Horizon re-renders through sectionRenderer, which REUSES a section fetch that
 * is already in flight — and after an add from a collection/search/recommendation card (which
 * dispatch without sections) that fetch started BEFORE our change, so the "re-render" would show
 * the pre-fee cart. With `sections`, Horizon cancels that fetch and morphs these directly.
 */
async function freshCartSections() {
  const ids = [
    ...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (el) => el.dataset.sectionId)),
  ].filter(Boolean);
  if (!ids.length) return undefined;
  try {
    const url = `${window.location.pathname}?sections=${ids.map(encodeURIComponent).join(',')}`;
    const r = await fetch(url, { credentials: 'same-origin', ...withTimeout() });
    return r.ok ? await r.json() : undefined;
  } catch (_e) {
    return undefined;
  }
}

/** Ask the theme's cart UI to re-render. source:SOURCE so our own listener ignores it; itemCount
 *  excludes auto-managed lines, so the badge shows real parts only (and is the same before and after
 *  a fee change). */
function dispatchRender(cart, sections) {
  const data = { source: SOURCE, itemCount: realCount(cart) };
  if (sections) data.sections = sections;
  document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, data));
}

async function releaseWhenRendered(token, cart) {
  if (cart) {
    for (let waited = 0; waited < BUSY_MAX_MS; waited += 100) {
      if (!activeLocks.has(token)) return;
      if (rowsShow(cart)) {
        releaseBusy(token);
        return;
      }
      await pause(100);
    }
    // Still showing an older cart (the section fetch failed, or the render was answered from a stale
    // one). Whatever fetch was in flight has settled by now, so one more plain request gets fresh rows.
    dispatchRender(cart);
  }
  releaseBusy(token);
}

// ── The pass ────────────────────────────────────────────────────────────────────────────────────

let running = false;
let rerunRequested = false;
/** Bounded self-heal: a pass that didn't settle gets up to this many delayed retries. */
const RETRIES = 2;
let retriesLeft = RETRIES;
let retryTimer = null;

/**
 * Keep exactly one correct fee line when the cart is under the minimum, and none when it isn't.
 * Sequential, idempotent, fail-open.
 */
async function reconcile() {
  if (running) {
    rerunRequested = true;
    return;
  }
  running = true;
  let settled = true;
  try {
    const cart = await getCart();
    if (!cart) {
      settled = false;
      return;
    }
    const plan = planFor(cart);
    if (!plan.changes) return;

    const token = setBusy();
    let changed = false;

    // 1. Remove fee lines we don't want (over the minimum, duplicates, a stale amount, or every
    //    fee line in remove-only mode). A key that went stale since the read (another app re-stamped
    //    the line's properties) makes change.js fail; the read-back below catches that and retries.
    for (const line of plan.remove) {
      if (await mutateOnce('/cart/change.js', { id: line.key, quantity: 0 })) changed = true;
    }

    // 2. One per cart, never per unit.
    if (plan.fixQty) {
      if (await mutateOnce('/cart/change.js', { id: plan.fixQty.key, quantity: 1 })) changed = true;
    }

    // 3. Add the fee if the cart is under the minimum and doesn't have one.
    if (plan.add) {
      const ok = await mutateOnce('/cart/add.js', {
        id: CONFIG.variantId,
        quantity: 1,
        properties: { [P_FEE]: 'true' },
      });
      if (ok) {
        changed = true;
      } else {
        // Fail loud (but never block). A Shopify Flow backstop can flag under-minimum orders that
        // reached checkout with no fee line — the same coverage gap core-deposit documents.
        console.warn(`[min-order-fee] could not add fee variant ${CONFIG.variantId}`);
      }
    }

    // Read back what we produced instead of trusting it: two tabs adding at once, a re-keyed line
    // or a twice-429'd change all leave a cart that still needs work, and nothing else would notice.
    const finalCart = await getCart();
    settled = Boolean(finalCart) && !planFor(finalCart).changes;

    if (changed) {
      // Always ask the UI to re-render after a change — even when the read-back failed, the rows on
      // screen are stale now (Horizon re-fetches the section itself if we have no HTML to give it).
      dispatchRender(finalCart || cart, await freshCartSections());
      releaseWhenRendered(token, finalCart);
    } else {
      releaseBusy(token); // nothing changed, nothing re-renders
    }
  } catch (_e) {
    settled = false; /* fail open — a fee hiccup must never block the cart */
  } finally {
    running = false;
    if (rerunRequested) {
      rerunRequested = false;
      reconcile();
    } else if (!settled && retriesLeft > 0) {
      retriesLeft--;
      clearTimeout(retryTimer);
      retryTimer = setTimeout(reconcile, 1500);
    } else if (settled) {
      retriesLeft = RETRIES;
    }
  }
}

const requestReconcile = debounce(reconcile, 150);

/**
 * Run a pass that starts AFTER whatever just happened, then check the cart holds exactly what it
 * should. Two attempts, so a pass that lost a race gets one more go before we give up on it.
 */
async function reconcileAndConfirm() {
  for (let attempt = 0; attempt < 2; attempt++) {
    // A pass already in flight read the cart before the change we care about — wait it out.
    for (let i = 0; running && i < 50; i++) await pause(100);
    await reconcile();
    const cart = await getCart();
    if (cart && !planFor(cart).changes) return true;
    await pause(400);
  }
  return false;
}

/** Cart events that should trigger a reconcile — but never our own re-render dispatch. */
function onCartEvent(event) {
  if (event && event.detail && event.detail.data && event.detail.data.source === SOURCE) return;
  requestReconcile();
}

/** Horizon's DialogComponent fires a NON-bubbling 'dialog:open' on the dialog element itself
 *  (assets/dialog.js), so it is only visible to a document listener in the CAPTURE phase. */
function onDialogOpen(e) {
  const t = e.target;
  const looksLikeCart =
    t &&
    t.matches &&
    (t.matches('cart-drawer-component, cart-drawer, dialog[id*="cart"]') ||
      (t.querySelector && t.querySelector('.cart-items__table-row')));
  if (looksLikeCart) requestReconcile();
}

function start() {
  document.addEventListener(ThemeEvents.cartUpdate, onCartEvent); // add-to-cart, qty change, remove
  document.addEventListener('cart:refresh', onCartEvent); // cart morph refresh
  // assets/live-pricing.js rewrites cart line prices from NDS ~3s after /cart loads. That can move
  // the cart across the threshold long after our pass ran, so it announces itself and we re-decide.
  document.addEventListener('cart:reprice', onCartEvent);
  document.addEventListener('dialog:open', onDialogOpen, true);

  // Safety-net pass once the page is idle: heals carts created before this shipped, or through a
  // surface that bypassed the reconciler. Only hit the network when a cart is actually rendered
  // here — a normal page load with no cart on screen costs zero requests.
  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
  idle(() => {
    if (document.querySelector('.cart-items__table-row')) reconcile();
  });
}

// ── Checkout waits for the cart to settle ───────────────────────────────────────────────────────
//
// Checkout is a native POST of #cart-form, and without this it races the cart in two ways:
//   - a quantity typed or stepped just before the click commits (Horizon debounces it 300 ms) only
//     AFTER the POST, so the fee is never re-decided — the shopper who adds enough to clear $35
//     still pays the $10, and one who drops below it pays nothing;
//   - the POST carries updates[] by POSITION from rows that can be a line off (see the header), so
//     a part's quantity can land on the fee.
// So the click is held: wait for pending quantity edits to commit through Horizon's own path, run
// a pass and confirm it, then REPLAY the same click — every other listener on the button (the
// pickup app, the form's own submit) still sees exactly one click — with the updates[] inputs
// disabled, since the server cart is now the authority. Fails OPEN like the rest of the module: if
// the cart can't be confirmed, checkout still goes ahead.

let replayingCheckout = false;
let guardingCheckout = false;

function pendingQuantityEdits() {
  return Array.from(document.querySelectorAll('#cart-form input[name="updates[]"]')).some(
    (i) => i.type !== 'hidden' && !i.disabled && i.value !== i.defaultValue
  );
}

/** Wait until every edited quantity has been committed (or rejected) by Horizon. */
async function settleQuantityEdits(timeoutMs = 4000) {
  const started = Date.now();
  if (pendingQuantityEdits()) await pause(350); // Horizon's 300 ms debounce, before it even asks
  while (pendingQuantityEdits() || document.querySelector('cart-items-component.cart-items-disabled')) {
    if (Date.now() - started > timeoutMs) return false;
    await pause(100);
  }
  return true;
}

/** The longest Checkout (or Buy it now) is ever held. After this it goes ahead regardless — a hung
 *  request must never trap a shopper on the cart. */
const GUARD_MAX_MS = 6000;

async function guardCheckout(button) {
  guardingCheckout = true;
  button.setAttribute('aria-busy', 'true');

  // Enter (or Go on a phone keyboard) in a quantity box submits WITHOUT blurring it, and Horizon only
  // commits a typed quantity on blur (snippets/quantity-selector.liquid) — so commit it the way a
  // mouse click on Checkout would have, then wait for it like any other edit.
  const focused = document.activeElement;
  if (focused && focused.matches && focused.matches('#cart-form input[name="updates[]"]') && focused.value !== focused.defaultValue) {
    focused.blur();
  }

  // `committed` keeps whatever the settle established even if the deadline cuts the pass short, so
  // a deadline can never replay an un-muted form that might be a line off.
  let committed = false;
  const work = (async () => {
    committed = await settleQuantityEdits();
    if (committed) await reconcileAndConfirm();
  })().catch(() => {
    /* fail open */
  });
  await Promise.race([work, pause(GUARD_MAX_MS)]);
  button.removeAttribute('aria-busy');
  guardingCheckout = false;
  // The pass's re-render may have replaced the button; replay on whichever one is live.
  const target = button.isConnected ? button : document.querySelector('button[name="checkout"]');
  if (!target || target.disabled) return;

  // Quantities are on the server now; don't let a stale positional form overwrite them. (If an edit
  // never committed, the form is left exactly as it was — the pre-guard behaviour.)
  const muted = [];
  if (committed) {
    for (const input of document.querySelectorAll('#cart-form input[name="updates[]"]')) {
      if (!input.disabled) {
        input.disabled = true;
        muted.push(input);
      }
    }
  }
  replayingCheckout = true;
  try {
    target.click();
  } finally {
    replayingCheckout = false;
    // The POST body (if the submit went ahead) was built synchronously inside click(). If something
    // vetoed it instead — the pickup app wanting a branch first — the shopper is still here and
    // needs working quantity boxes; the same goes for a page restored from the back/forward cache.
    setTimeout(() => muted.forEach((input) => (input.disabled = false)), 0);
  }
}

function startCheckoutGuard() {
  document.addEventListener(
    'click',
    (e) => {
      if (replayingCheckout) return;
      const btn = e.target && e.target.closest ? e.target.closest('button[name="checkout"]') : null;
      if (!btn || btn.disabled) return;
      // Capture phase + stopImmediatePropagation: nothing else sees this click; it sees the replay.
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!guardingCheckout) guardCheckout(btn);
    },
    true
  );
}

// ── "Buy it now" through the cart (settings.min_order_fee_express == 'cart') ────────────────────
//
// sections/product-info-clean.liquid renders [data-fee-buy-now] in place of Shopify's express
// buttons on parts that could owe the fee. Shopify's own Buy it now builds a checkout from the
// product form alone and never touches the cart — the only place the fee exists. This one adds the
// part to the cart, runs the same reconcile pass the cart runs, confirms the result, and only then
// goes to checkout. If it can't confirm the fee is right it lands on /cart instead (which
// reconciles on load and explains the fee), so a hiccup costs a click but never the fee. Unlike
// Shopify's button, anything already in the cart goes to checkout with it.

function resetBuyNow(button) {
  button.disabled = false;
  button.removeAttribute('aria-busy');
  if (button.dataset.label) button.textContent = button.dataset.label;
}

async function buyNow(button) {
  const form = button.closest('form');
  if (!form || button.disabled) return;
  if (!button.dataset.label) button.dataset.label = button.textContent;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.textContent = 'One moment…';

  let added = false;
  try {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      body: new FormData(form), // the same fields Add to cart posts: id, quantity, properties[]
      ...withTimeout(),
    });
    added = r.ok;
  } catch (_e) {
    added = false;
  }

  if (!added) {
    // Out of stock, quantity over what's available, network — hand over to the normal Add to cart
    // path so the theme's own error message shows, rather than inventing a second error UI here.
    resetBuyNow(button);
    const atc = form.querySelector('button[name="add"]');
    if (typeof form.requestSubmit === 'function') form.requestSubmit(atc || undefined);
    return;
  }

  let confirmed = false;
  try {
    // Outside USD the module never reconciles (see currencyOk), so there is nothing to wait for. A
    // pass that can't finish in time lands the shopper on /cart, never on a checkout that may be
    // missing the fee.
    confirmed = currencyOk ? await Promise.race([reconcileAndConfirm(), pause(GUARD_MAX_MS).then(() => false)]) : true;
  } catch (_e) {
    confirmed = false;
  }
  // data-fee-buy-now-dest is /cart when the cart needs a pickup branch chosen first (the same routing
  // the drawer's Checkout does — see sections/product-info-clean.liquid).
  window.location.assign(confirmed ? button.dataset.feeBuyNowDest || '/checkout' : '/cart');
}

/** Delegated, so it keeps working after the OEM/AM flip swaps the product section's markup. */
function startBuyNow() {
  document.addEventListener('click', (e) => {
    const btn = e.target && e.target.closest ? e.target.closest('[data-fee-buy-now]') : null;
    if (!btn) return;
    e.preventDefault();
    buyNow(btn);
  });
  // Back from checkout restores this page from the back/forward cache exactly as it was left — with
  // the button still disabled on "One moment…".
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) document.querySelectorAll('[data-fee-buy-now]').forEach(resetBuyNow);
  });
}

// The threshold is a plain USD figure but cart prices come back in the PRESENTMENT currency, so
// in any other market the comparison would be cents-to-cents across two currencies. Stay out —
// except to remove a fee line, which is right in any currency.
const currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
const currencyOk = !currency || currency === 'USD';

function init() {
  start();
  startCheckoutGuard();
}

if (CONFIG) {
  if (CONFIG.mode === 'fee') startBuyNow();
  if (CONFIG.mode === 'remove' || currencyOk) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  }
}
